import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload, FileText, Printer, Trash2, Ship, Package, AlertTriangle,
  CheckCircle, XCircle, RefreshCw, Download, Plus, StickyNote,
  Scissors, FileDown, Zap, Globe, Archive, ChevronDown, ChevronUp,
  User, MapPin, Clock, Edit2, Save, X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const BRANDS = [
  { id: "extreme",    label: "Extreme",    store: "siccnh-ht.myshopify.com" },
  { id: "oblik",      label: "OBLIK",      store: "wearoblik.myshopify.com" },
  { id: "millennium", label: "Millennium", store: null },
];

// ── Brand Toggle ──────────────────────────────────────────────────────────────
function BrandToggle({ brand, onChange }) {
  return (
    <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
      {BRANDS.map(b => (
        <button key={b.id} onClick={() => onChange(b.id)}
          className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
            brand === b.id ? "bg-slate-800 text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}>
          {b.label}
          {!b.store && <span className="ml-1.5 text-[10px] opacity-50">Soon</span>}
        </button>
      ))}
    </div>
  );
}

// ── Edit Order Modal ──────────────────────────────────────────────────────────
function EditOrderModal({ order, onSave, onClose }) {
  const [form, setForm] = useState({
    name:    order.customer.name,
    phone:   order.customer.phone,
    email:   order.customer.email,
    line1:   order.address.line1,
    line2:   order.address.line2,
    city:    order.address.city,
    state:   order.address.state,
    postal:  order.address.postal,
    country: order.address.country,
    country_code: order.address.country_code,
  });

  const charWarn = (val) => val.length > 35 ? "border-red-400 bg-red-50" : "";

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-semibold text-slate-800">Edit Order — {order.order_number}</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-slate-400" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Contact</div>
          <div className="grid grid-cols-2 gap-3">
            {[["name","Name"],["phone","Phone"],["email","Email"]].map(([k,l]) => (
              <div key={k} className={k==="email"?"col-span-2":""}>
                <label className="text-xs text-slate-500 mb-1 block">{l}</label>
                <input value={form[k]} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300" />
              </div>
            ))}
          </div>
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mt-2">Address <span className="text-amber-500 font-normal normal-case">(max 35 chars per line)</span></div>
          <div className="grid grid-cols-2 gap-3">
            {[["line1","Address Line 1"],["line2","Address Line 2"],["city","City"],["state","State"],["postal","Postal Code"],["country_code","Country Code"]].map(([k,l]) => (
              <div key={k} className={k==="line1"||k==="line2"?"col-span-2":""}>
                <label className="text-xs text-slate-500 mb-1 block">{l}</label>
                <input value={form[k]} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))}
                  className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300 font-mono ${charWarn(form[k]||"")}`} />
                {(k==="line1"||k==="line2") && <div className={`text-[10px] text-right mt-0.5 ${(form[k]||"").length>35?"text-red-500":"text-slate-400"}`}>{(form[k]||"").length}/35</div>}
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="bg-slate-800 text-white hover:bg-slate-700"
            onClick={() => {
              const updated = {
                ...order,
                customer: { ...order.customer, name: form.name, phone: form.phone, email: form.email },
                address:  { ...order.address,  line1: form.line1, line2: form.line2, city: form.city, state: form.state, postal: form.postal, country_code: form.country_code },
              };
              onSave(updated);
            }}>
            <Save className="w-3.5 h-3.5 mr-1" />Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Ship Orders Tab ───────────────────────────────────────────────────────────
function ShipOrdersTab({ brand }) {
  const [orders, setOrders]           = useState([]);
  const [editedOrders, setEditedOrders] = useState({});  // overrides by order id
  const [loading, setLoading]         = useState(false);
  const [selected, setSelected]       = useState(new Set());
  const [expanded, setExpanded]       = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoiceB64, setInvoiceB64]   = useState(null);
  const [generating, setGenerating]   = useState(false);
  const [results, setResults]         = useState([]);
  const [tokenOk, setTokenOk]         = useState(null);
  const [pickupDone, setPickupDone]   = useState(false);
  const [pickupRef, setPickupRef]     = useState(null);
  const [schedulingPickup, setSchedulingPickup] = useState(false);

  const brandInfo = BRANDS.find(b => b.id === brand);

  const checkToken = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/shopify/token-status`);
      setTokenOk(r.data[brand]?.connected || false);
    } catch { setTokenOk(false); }
  }, [brand]);

  const loadOrders = useCallback(async () => {
    if (!tokenOk) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/shopify/orders?brand=${brand}`);
      setOrders(r.data.orders || []);
      setEditedOrders({});
    } catch (e) {
      const msg = e.response?.data?.detail || "";
      // If Shopify token is broken, show reconnect UI
      if (e.response?.status === 401 || msg.includes("Not Found") || msg.includes("not connected")) {
        setTokenOk(false);
      } else {
        toast.error(msg || "Failed to load orders");
      }
    }
    setLoading(false);
  }, [brand, tokenOk]);

  useEffect(() => { checkToken(); }, [checkToken]);
  useEffect(() => { if (tokenOk) loadOrders(); }, [tokenOk, loadOrders]);

  const getOrder = (o) => editedOrders[o.id] || o;

  const toggleSelect = (id) => setSelected(prev => { const s=new Set(prev); s.has(id)?s.delete(id):s.add(id); return s; });
  const toggleAll = () => setSelected(prev => prev.size===orders.length ? new Set() : new Set(orders.map(o=>o.id)));

  const handleInvoiceUpload = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setInvoiceFile(file);
    const reader = new FileReader();
    reader.onload = ev => setInvoiceB64(ev.target.result.split(",")[1]);
    reader.readAsDataURL(file);
  };

  const handleGenerate = async () => {
    if (selected.size === 0) { toast.error("Select at least one order"); return; }
    if (!invoiceB64) { toast.error("Upload the invoice PDF first"); return; }
    setGenerating(true);
    const selectedOrders = orders.filter(o => selected.has(o.id)).map(o => getOrder(o));
    try {
      const r = await axios.post(`${API}/shipping/generate`, { brand, orders: selectedOrders, invoice_b64: invoiceB64 });
      setResults(r.data.results || []);
      if (r.data.errors?.length) r.data.errors.forEach(e => toast.error(`${e.order_number}: ${e.error}`));
      if (r.data.results?.length) {
        toast.success(`${r.data.results.length} label(s) generated`);
        // Check fulfillment status for each result
        const fulfillFails = r.data.results.filter(res => res.fulfill && !res.fulfill.success);
        if (fulfillFails.length > 0) {
          fulfillFails.forEach(f => toast.warning(`⚠ ${f.order_number}: Shopify fulfillment issue — ${f.fulfill?.error || 'unknown'}`));
        }
        loadOrders();
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    setGenerating(false);
  };

  const schedulePickup = async () => {
    setSchedulingPickup(true);
    try {
      const r = await axios.post(`${API}/shipping/pickup`, { brand });
      if (r.data.success) {
        setPickupDone(true);
        setPickupRef(r.data.confirmation_no);
        toast.success(`Pickup scheduled — ${r.data.confirmation_no}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Pickup scheduling failed");
    }
    setSchedulingPickup(false);
  };

  if (tokenOk === false) return (
    <div className="bg-white rounded-xl border p-10 text-center max-w-md mx-auto mt-8">
      <Ship className="w-10 h-10 mx-auto mb-3 text-slate-300" />
      <h3 className="text-lg font-semibold text-slate-800 mb-2">Shopify Not Connected</h3>
      <p className="text-sm text-slate-500 mb-6">
        {brandInfo?.store ? `Connect to ${brandInfo.store}` : "Connect your store to load orders."}
      </p>
      <a href={`${API}/shopify/auth?brand=${brand}`}
        className="inline-flex items-center gap-2 bg-slate-800 text-white px-6 py-3 rounded-lg text-sm font-semibold hover:bg-slate-700 transition-colors">
        🔗 Connect {brandInfo?.label} to Shopify
      </a>
      <p className="text-xs text-slate-400 mt-4">
        You will be redirected to Shopify to approve access, then brought back here automatically.
      </p>
    </div>
  );

  return (
    <div className="space-y-4">
      {tokenOk === null && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-500 flex items-center gap-2">
          <RefreshCw className="w-3.5 h-3.5 animate-spin" />Checking Shopify connection...
        </div>
      )}
      {editingOrder && (
        <EditOrderModal
          order={editingOrder}
          onClose={() => setEditingOrder(null)}
          onSave={(updated) => {
            setEditedOrders(prev => ({...prev, [updated.id]: updated}));
            setEditingOrder(null);
            toast.success("Order details updated");
          }}
        />
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={loadOrders} disabled={loading}>
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading?"animate-spin":""}`} />Refresh
        </Button>
        <span className="text-sm text-slate-500">{orders.length} unfulfilled order{orders.length!==1?"s":""}</span>
        <a href={`${API}/shopify/reauth?brand=${brand}`}
          className="text-[11px] text-blue-500 hover:text-blue-700 underline"
          title="Re-authorize Shopify to update permissions (required after scope changes)">
          Re-authorize
        </a>
        <div className="ml-auto flex items-center gap-2">
          <label className="cursor-pointer">
            <input type="file" accept=".pdf" onChange={handleInvoiceUpload} className="hidden" />
            <Button asChild variant="outline" size="sm">
              <span>{invoiceFile
                ? <><CheckCircle className="w-3.5 h-3.5 mr-1 text-emerald-500" />{invoiceFile.name}</>
                : <><Upload className="w-3.5 h-3.5 mr-1" />Upload Invoice (Pre-AWB)</>}</span>
            </Button>
          </label>
          {!pickupDone ? (
            <Button onClick={schedulePickup} disabled={schedulingPickup}
              variant="outline" className="border-green-300 text-green-700 hover:bg-green-50">
              {schedulingPickup ? <><RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin"/>Scheduling...</> : <>📦 Schedule Pickup</>}
            </Button>
          ) : (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-1.5 text-xs text-green-700 font-medium">
              <CheckCircle className="w-3.5 h-3.5" />Pickup Scheduled {pickupRef && `· ${pickupRef}`}
            </div>
          )}
          {selected.size > 0 && (
            <Button onClick={handleGenerate} disabled={generating||!invoiceB64} className="bg-slate-800 text-white hover:bg-slate-700">
              {generating ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Generating...</> : <><Zap className="w-4 h-4 mr-2" />Generate {selected.size} Label{selected.size>1?"s":""}</>}
            </Button>
          )}
        </div>
      </div>

      {!invoiceFile && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 text-xs text-amber-700 flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
          Upload the combined invoice PDF (no AWB) before generating labels. This is attached to every shipment as ETD.
        </div>
      )}

      {results.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3">
          <div className="font-semibold text-emerald-800 text-sm mb-2">✓ {results.length} label{results.length>1?"s":""} generated</div>
          <div className="space-y-1.5">
            {results.map((r,i) => {
              const orderObj = orders.find(o => o.order_number === r.order_number) || {};
              return (
                <div key={i} className="flex items-center gap-3 text-xs font-mono">
                  <span className="text-emerald-700 font-bold">{r.order_number}</span>
                  <span className="text-slate-500">AWB:</span>
                  <span className="text-slate-800 font-bold">{r.awb}</span>
                  {r.fulfill?.success
                    ? <span className="text-emerald-600 flex items-center gap-1"><CheckCircle className="w-3 h-3"/>Fulfilled</span>
                    : <>
                        <span className="text-amber-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3"/>Not fulfilled</span>
                        <button
                          className="text-blue-600 hover:text-blue-800 underline text-[11px]"
                          onClick={async () => {
                            try {
                              const res = await axios.post(`${API}/shopify/fulfill`, {
                                brand, shopify_id: orderObj.id || r.shopify_id,
                                order_number: r.order_number, tracking_number: r.awb
                              });
                              if (res.data.success) {
                                toast.success(`${r.order_number} fulfilled!`);
                                setResults(prev => prev.map(p => p.order_number === r.order_number
                                  ? {...p, fulfill: {success: true, message: res.data.message}} : p));
                              }
                            } catch (e) { toast.error(`Fulfill failed: ${e.response?.data?.detail || e.message}`); }
                          }}>
                          Retry Fulfill
                        </button>
                      </>}
                </div>
              );
            })}
          </div>
          {results.every(r => r.fulfill?.success) ? (
            <p className="text-xs text-emerald-600 mt-2">Orders marked fulfilled in Shopify with tracking. Go to Downloads tab for labels and print documents.</p>
          ) : (
            <p className="text-xs text-amber-600 mt-2">Some orders were not auto-fulfilled. Use "Retry Fulfill" or check /api/shipping/fedex-debug for details.</p>
          )}
        </div>
      )}

      {orders.length === 0 && !loading ? (
        <div className="bg-white rounded-xl border p-12 text-center">
          <CheckCircle className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <h3 className="text-lg font-semibold text-slate-700 mb-1">All caught up</h3>
          <p className="text-sm text-slate-500">No unfulfilled orders right now.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-2.5 border-b bg-slate-50">
            <input type="checkbox" checked={selected.size===orders.length&&orders.length>0} onChange={toggleAll} className="rounded" />
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              {selected.size > 0 ? `${selected.size} selected` : "Select all"}
            </span>
          </div>
          {orders.map(order => {
            const o = getOrder(order);
            const isSelected = selected.has(order.id);
            const isExpanded = expanded === order.id;
            const isEdited   = !!editedOrders[order.id];
            const watches = o.items.filter(i=>i.type==="watch");
            const straps  = o.items.filter(i=>i.type==="strap");
            return (
              <div key={order.id} className={`border-b last:border-b-0 transition-colors ${isSelected?"bg-blue-50/40":"bg-white"}`}>
                <div className="flex items-start gap-3 px-4 py-3">
                  <input type="checkbox" checked={isSelected} onChange={()=>toggleSelect(order.id)} className="rounded mt-1 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-bold text-sm text-slate-800">{o.order_number}</span>
                      {isEdited && <span className="text-[10px] bg-amber-100 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5 font-semibold">Edited</span>}
                      <span className="text-slate-400">·</span>
                      <span className="text-sm text-slate-700">{o.customer.name}</span>
                      <span className="text-slate-400">·</span>
                      <span className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{o.address.city}, {o.address.country_code}</span>
                      <span className="ml-auto font-mono text-sm font-semibold text-slate-700">${o.total_price.toFixed(2)}</span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {watches.map((w,i) => <span key={i} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-xs px-2 py-0.5 rounded-full font-medium">⌚ {w.name}{w.variant?` — ${w.variant}`:""} × {w.quantity}</span>)}
                      {straps.map((s,i) => <span key={i} className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-200 text-xs px-2 py-0.5 rounded-full font-medium">🪢 {s.name}{s.variant?` — ${s.variant}`:""} × {s.quantity}</span>)}
                    </div>
                    <div className="mt-1.5 flex items-center gap-3 text-xs text-slate-400">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(o.created_at).toLocaleDateString("en-IN",{day:"2-digit",month:"short"})}</span>
                      <span>{o.shipping_method}</span>
                      <span>{o.total_weight_kg} kg</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 mt-0.5 flex-shrink-0">
                    <button onClick={()=>setEditingOrder(o)} title="Edit order details"
                      className="text-slate-400 hover:text-slate-600 p-1 rounded hover:bg-slate-100">
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={()=>setExpanded(isExpanded?null:order.id)} className="text-slate-400 hover:text-slate-600 p-1">
                      {isExpanded?<ChevronUp className="w-4 h-4" />:<ChevronDown className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {isExpanded && (
                  <div className="px-4 pb-4 pl-11 bg-slate-50/60 border-t">
                    <div className="grid grid-cols-2 gap-4 mt-3 text-xs">
                      <div>
                        <div className="font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Ship to</div>
                        <div className="text-slate-700 space-y-0.5 font-mono">
                          <div>{o.address.line1}{o.address.line1.length>35&&<span className="text-red-500 ml-1">⚠ over 35</span>}</div>
                          {o.address.line2&&<div>{o.address.line2}{o.address.line2.length>35&&<span className="text-red-500 ml-1">⚠ over 35</span>}</div>}
                          <div>{o.address.city}, {o.address.state} {o.address.postal}</div>
                          <div>{o.address.country} ({o.address.country_code})</div>
                        </div>
                      </div>
                      <div>
                        <div className="font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Contact</div>
                        <div className="text-slate-700 space-y-0.5 text-xs">
                          <div className="flex items-center gap-1.5"><User className="w-3 h-3 text-slate-400" />{o.customer.name}</div>
                          <div>{o.customer.email}</div>
                          <div>{o.customer.phone}</div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3">
                      <div className="font-semibold text-slate-500 uppercase tracking-wide text-xs mb-1.5">Line Items</div>
                      <table className="w-full text-xs">
                        <thead><tr className="text-slate-400"><th className="text-left pb-1">Item</th><th className="text-left pb-1">HS Code</th><th className="text-left pb-1">Description</th><th className="text-right pb-1">Qty</th><th className="text-right pb-1">Value</th></tr></thead>
                        <tbody>{o.items.map((item,i)=>(
                          <tr key={i} className="text-slate-700">
                            <td className="py-0.5">{item.name}{item.variant&&` — ${item.variant}`}</td>
                            <td className="font-mono text-slate-500">{item.hs_code}</td><td className="text-slate-600 text-xs">{item.description}</td>
                            <td className="text-right">{item.quantity}</td>
                            <td className="text-right">${item.line_total.toFixed(2)}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Worksheets Tab ────────────────────────────────────────────────────────────
function WorksheetsTab({ brand }) {
  const [worksheets, setWorksheets] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [uploading, setUploading]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/export-assist/worksheets?brand=${brand}`); setWorksheets(r.data.worksheets||[]); }
    catch { toast.error("Failed to load worksheets"); }
    setLoading(false);
  }, [brand]);

  useEffect(()=>{ load(); },[load]);

  const handleUpload = async (e) => {
    const files = e.target.files; if (!files?.length) return;
    setUploading(true);
    let n=0;
    for (const file of files) {
      const form = new FormData(); form.append("file", file);
      try { await axios.post(`${API}/export-assist/worksheets?brand=${brand}`,form); n++; }
      catch { toast.error(`Failed: ${file.name}`); }
    }
    if (n) toast.success(`${n} worksheet(s) uploaded`);
    e.target.value=""; setUploading(false); load();
  };

  const handleDelete = async (name) => {
    try { await axios.delete(`${API}/export-assist/worksheets/${encodeURIComponent(name)}?brand=${brand}`); toast.success(`${name} deleted`); setWorksheets(prev=>prev.filter(w=>w.name!==name)); }
    catch { toast.error("Delete failed"); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600"/></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Watch worksheets for customs clearance (EU + US only). File name = product name.</p>
        <label className="cursor-pointer">
          <input type="file" accept=".pdf" multiple onChange={handleUpload} className="hidden"/>
          <Button asChild disabled={uploading} size="sm"><span><Plus className="w-4 h-4 mr-1"/>{uploading?"Uploading...":"Upload Worksheets"}</span></Button>
        </label>
      </div>
      <div className="bg-white rounded-xl border overflow-hidden">
        {worksheets.length===0 ? (
          <div className="p-8 text-center text-slate-400"><StickyNote className="w-10 h-10 mx-auto mb-2 text-slate-300"/><p className="text-sm">No worksheets uploaded yet.</p></div>
        ) : (
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase">File Name</TableHead>
              <TableHead className="text-xs uppercase">Product</TableHead>
              <TableHead className="text-xs uppercase">Size</TableHead>
              <TableHead className="text-xs uppercase">Uploaded</TableHead>
              <TableHead className="text-xs uppercase w-20 text-center">Remove</TableHead>
            </TableRow></TableHeader>
            <TableBody>{worksheets.map((ws,i)=>(
              <TableRow key={i}>
                <TableCell className="font-mono text-xs">{ws.name}</TableCell>
                <TableCell className="text-sm">{ws.product_name}</TableCell>
                <TableCell className="text-xs text-slate-500">{ws.size_kb} KB</TableCell>
                <TableCell className="text-xs text-slate-500">{ws.uploaded_at?new Date(ws.uploaded_at).toLocaleDateString():"-"}</TableCell>
                <TableCell className="text-center">
                  <Button variant="ghost" size="sm" onClick={()=>handleDelete(ws.name)} className="text-red-400 hover:text-red-600 hover:bg-red-50 h-7 w-7 p-0"><Trash2 className="w-3.5 h-3.5"/></Button>
                </TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

// ── Downloads Tab ─────────────────────────────────────────────────────────────
function DownloadsTab({ brand }) {
  const [dlTab, setDlTab]           = useState("invoices");
  const [labels, setLabels]         = useState([]);
  const [labelDate, setLabelDate]   = useState(() => new Date().toISOString().slice(0,10));
  const [labelDates, setLabelDates] = useState([]);
  const [selectedLabels, setSelectedLabels] = useState(new Set());
  const [labelsLoading, setLabelsLoading]   = useState(false);
  const [downloading, setDownloading]       = useState(null);
  const [invoices, setInvoices]     = useState([]);
  const [hasInvoiceData, setHasInvoiceData] = useState(false);
  const [uploadingInv, setUploadingInv]     = useState(false);
  const [invDownloading, setInvDownloading] = useState(null);
  const [session, setSession]       = useState(null);
  const [printing, setPrinting]     = useState(null);
  // Shipping docs state
  const [shipDate, setShipDate]     = useState(() => new Date().toISOString().slice(0,10));
  const [uploadingShip, setUploadingShip] = useState(false);

  const loadLabelDates = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/shipping/label-dates?brand=${brand}`);
      setLabelDates(r.data.dates || []);
    } catch {}
  }, [brand]);

  const loadLabels = useCallback(async (date) => {
    setLabelsLoading(true);
    try {
      const r = await axios.get(`${API}/shipping/labels?brand=${brand}&date=${date || labelDate}`);
      setLabels(r.data.labels || []);
    } catch { toast.error("Failed to load labels"); }
    setLabelsLoading(false);
  }, [brand, labelDate]);

  const loadInvoiceSession = useCallback(async () => {
    try { const r = await axios.get(`${API}/export-assist/generate-session?brand=${brand}`); setInvoices(r.data.invoices||[]); setHasInvoiceData(r.data.has_data); }
    catch {}
  }, [brand]);

  const loadMatchSession = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/export-assist/session?brand=${brand}`);
      setSession(r.data);
    } catch { setSession(null); }
  }, [brand]);

  useEffect(() => { loadLabels(labelDate); }, [labelDate, brand, loadLabels]);
  useEffect(() => { loadLabelDates(); loadInvoiceSession(); loadMatchSession(); }, [loadLabelDates, loadInvoiceSession, loadMatchSession]);

  function triggerDownload(blob, name, mime) {
    const url = URL.createObjectURL(new Blob([blob],{type:mime}));
    const a = document.createElement("a"); a.href=url; a.download=name; a.click(); URL.revokeObjectURL(url);
  }

  // ── Invoice split ──
  const handleInvoiceUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploadingInv(true);
    const form = new FormData(); form.append("file", file);
    try { const r = await axios.post(`${API}/export-assist/generate-split?brand=${brand}`,form); toast.success(r.data.message); loadInvoiceSession(); }
    catch (err) { toast.error(err.response?.data?.detail||"Upload failed"); }
    e.target.value=""; setUploadingInv(false);
  };

  const downloadOneInvoice = async (inv) => {
    setInvDownloading(inv.invoice_number);
    try { const r = await axios.get(`${API}/export-assist/generate-download/${encodeURIComponent(inv.invoice_number)}?brand=${brand}`,{responseType:"blob"}); triggerDownload(r.data,inv.filename,"application/pdf"); }
    catch { toast.error("Download failed"); }
    setInvDownloading(null);
  };

  const downloadAllInvoices = async () => {
    setInvDownloading("all");
    try { const r = await axios.get(`${API}/export-assist/generate-download-all?brand=${brand}`,{responseType:"blob"}); triggerDownload(r.data,"invoices.zip","application/zip"); toast.success("Downloaded"); }
    catch { toast.error("Download failed"); }
    setInvDownloading(null);
  };

  // ── Labels download ──
  const toggleLabelSelect = (orderNo) => setSelectedLabels(prev => { const s=new Set(prev); s.has(orderNo)?s.delete(orderNo):s.add(orderNo); return s; });
  const toggleAllLabels   = () => setSelectedLabels(prev => prev.size===labels.length ? new Set() : new Set(labels.map(l=>l.order_number)));

  const handleLabelDownload = async (format) => {
    const orderNos = selectedLabels.size > 0 ? [...selectedLabels] : labels.map(l=>l.order_number);
    if (orderNos.length===0) return;
    setDownloading(format);
    try {
      const r = await axios.post(`${API}/shipping/labels/download`,{brand,order_numbers:orderNos,format},{responseType:"blob"});
      const ext = format==="zip"?"zip":"pdf"; const mime = ext==="zip"?"application/zip":"application/pdf";
      triggerDownload(r.data,`labels.${ext}`,mime);
      toast.success("Downloaded");
    } catch { toast.error("Download failed"); }
    setDownloading(null);
  };

  // ── Shipping docs — upload invoice, auto-load labels from DB ──
  const handleShipInvoiceUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploadingShip(true);
    const form = new FormData(); form.append("file", file);
    try {
      const r = await axios.post(`${API}/export-assist/upload-invoices-autosync?brand=${brand}&label_date=${shipDate}`, form);
      toast.success(r.data.message || "Matched!");
      loadMatchSession();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    e.target.value = "";
    setUploadingShip(false);
  };

  const handleClearSession = async () => {
    if (!window.confirm("Clear the current shipping documents session?\n\nThis only clears the matching session — your permanent labels in the Labels tab are safe.")) return;
    try {
      await axios.delete(`${API}/export-assist/session?brand=${brand}`);
      toast.success("Session cleared");
      setSession(null);
    } catch { toast.error("Clear failed"); }
  };

  const handlePrint = async (type) => {
    setPrinting(type);
    try {
      const url = type==="normal" ? `${API}/export-assist/print/normal?brand=${brand}` : `${API}/export-assist/print/labels?brand=${brand}`;
      const r = await axios.get(url,{responseType:"blob"});
      triggerDownload(r.data, type==="normal"?"export_normal_paper.pdf":"export_label_paper.pdf","application/pdf");
      toast.success(`${type==="normal"?"Normal paper":"Label paper"} PDF downloaded`);
    } catch (err) { toast.error(err.response?.data?.detail||"Print generation failed"); }
    setPrinting(null);
  };

  const orders = session?.orders || [];
  const hasMatched = orders.length > 0;

  const DL_TABS = [
    {id:"invoices",label:"Invoices",icon:FileText},
    {id:"labels",label:"Labels",icon:Package},
    {id:"print",label:"Shipping Documents",icon:Printer},
  ];

  return (
    <div className="space-y-4">
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        {DL_TABS.map(t=>(
          <button key={t.id} onClick={()=>setDlTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${dlTab===t.id?"bg-white text-slate-800 shadow-sm":"text-slate-500 hover:text-slate-700"}`}>
            <t.icon className="w-3.5 h-3.5"/>{t.label}
          </button>
        ))}
      </div>

      {/* INVOICES */}
      {dlTab==="invoices" && (
        <div className="space-y-4">
          <p className="text-sm text-slate-500">Upload the final combined invoice PDF (with AWB) to split into individual files.</p>
          <div className="bg-white rounded-xl border p-5 flex items-center gap-4">
            <label className="cursor-pointer flex-1">
              <input type="file" accept=".pdf" onChange={handleInvoiceUpload} className="hidden"/>
              <Button asChild disabled={uploadingInv} variant="outline" className="w-full">
                <span>{uploadingInv?<><RefreshCw className="w-4 h-4 mr-2 animate-spin"/>Splitting...</>:<><Upload className="w-4 h-4 mr-2"/>Upload Final Invoice PDF (with AWB)</>}</span>
              </Button>
            </label>
            {hasInvoiceData && (
              <div className="flex gap-2">
                <Button onClick={downloadAllInvoices} disabled={!!invDownloading} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Download className="w-4 h-4 mr-1"/>{invDownloading==="all"?"Zipping...":`Download All (${invoices.length})`}
                </Button>
                <Button variant="destructive" onClick={async()=>{ await axios.delete(`${API}/export-assist/generate-session?brand=${brand}`); setInvoices([]); setHasInvoiceData(false); toast.success("Cleared"); }} size="sm"><Trash2 className="w-3.5 h-3.5 mr-1"/>Clear</Button>
              </div>
            )}
          </div>
          {invoices.length>0 && (
            <div className="bg-white rounded-xl border overflow-hidden">
              <div className="px-5 py-3 border-b"><h3 className="font-semibold text-slate-800 text-sm">Split Invoices ({invoices.length})</h3></div>
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase w-10">#</TableHead>
                  <TableHead className="text-xs uppercase">Invoice Number</TableHead>
                  <TableHead className="text-xs uppercase">File</TableHead>
                  <TableHead className="text-xs uppercase">Size</TableHead>
                  <TableHead className="text-xs uppercase text-center w-28">Download</TableHead>
                </TableRow></TableHeader>
                <TableBody>{invoices.map((inv,i)=>(
                  <TableRow key={i}>
                    <TableCell className="text-xs text-slate-400">{i+1}</TableCell>
                    <TableCell className="font-mono text-sm font-semibold">{inv.invoice_number}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-500">{inv.filename}</TableCell>
                    <TableCell className="text-xs text-slate-500">{inv.size_kb} KB</TableCell>
                    <TableCell className="text-center">
                      <Button variant="ghost" size="sm" onClick={()=>downloadOneInvoice(inv)} disabled={invDownloading===inv.invoice_number}>
                        <FileDown className="w-3.5 h-3.5 mr-1"/>{invDownloading===inv.invoice_number?"...":"Download"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </div>
          )}
          {!hasInvoiceData && <div className="bg-white rounded-xl border p-10 text-center"><Scissors className="w-10 h-10 mx-auto mb-3 text-slate-300"/><h3 className="text-lg font-semibold text-slate-700 mb-1">No invoices split yet</h3><p className="text-sm text-slate-500">Upload after generating labels so AWBs are included.</p></div>}
        </div>
      )}

      {/* LABELS */}
      {dlTab==="labels" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <p className="text-sm text-slate-500">{labels.length} label(s) for</p>
              <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
                {labelDates.length > 0 ? labelDates.map(d => (
                  <button key={d.date} onClick={() => setLabelDate(d.date)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      labelDate === d.date ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
                    }`}>
                    {new Date(d.date + "T12:00:00").toLocaleDateString("en-IN", {day:"2-digit", month:"short"})}
                    <span className="ml-1 text-[10px] text-slate-400">({d.count})</span>
                  </button>
                )) : (
                  <span className="px-3 py-1.5 text-xs text-slate-400">Today</span>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => loadLabels(labelDate)} disabled={labelsLoading}>
                <RefreshCw className={`w-3.5 h-3.5 mr-1 ${labelsLoading?"animate-spin":""}`}/>Refresh
              </Button>
            </div>
          </div>
          {labels.length>0 && (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-slate-500">{selectedLabels.size>0?`${selectedLabels.size} selected`:"All labels"}</span>
                <div className="ml-auto flex gap-2">
                  <Button onClick={()=>handleLabelDownload("combined")} disabled={!!downloading} variant="outline" size="sm">
                    <Download className="w-3.5 h-3.5 mr-1"/>{downloading==="combined"?"...":"Combined PDF"}
                  </Button>
                  <Button onClick={()=>handleLabelDownload("zip")} disabled={!!downloading} variant="outline" size="sm">
                    <Archive className="w-3.5 h-3.5 mr-1"/>{downloading==="zip"?"...":"ZIP"}
                  </Button>
                </div>
              </div>
              <div className="bg-white rounded-xl border overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-2.5 border-b bg-slate-50">
                  <input type="checkbox" checked={selectedLabels.size===labels.length&&labels.length>0} onChange={toggleAllLabels} className="rounded"/>
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{selectedLabels.size>0?`${selectedLabels.size} selected`:"Select for individual download"}</span>
                </div>
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead className="w-10"></TableHead>
                    <TableHead className="text-xs uppercase">Order</TableHead>
                    <TableHead className="text-xs uppercase">Customer</TableHead>
                    <TableHead className="text-xs uppercase">Country</TableHead>
                    <TableHead className="text-xs uppercase">AWB</TableHead>
                    <TableHead className="text-xs uppercase">Generated</TableHead>
                    <TableHead className="text-xs uppercase text-center w-28">Fulfill</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>{labels.map((l,i)=>(
                    <TableRow key={i} className={selectedLabels.has(l.order_number)?"bg-blue-50/40":""}>
                      <TableCell><input type="checkbox" checked={selectedLabels.has(l.order_number)} onChange={()=>toggleLabelSelect(l.order_number)} className="rounded"/></TableCell>
                      <TableCell className="font-mono font-bold text-xs text-slate-800">{l.order_number}</TableCell>
                      <TableCell className="text-xs">{l.customer}</TableCell>
                      <TableCell className="text-xs">{l.country}</TableCell>
                      <TableCell className="font-mono text-xs text-emerald-700 font-bold">{l.awb}</TableCell>
                      <TableCell className="text-xs text-slate-500">{new Date(l.generated_at).toLocaleTimeString()}</TableCell>
                      <TableCell className="text-center">
                        <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 h-7 text-[11px]"
                          onClick={async () => {
                            try {
                              const res = await axios.post(`${API}/shopify/fulfill`, {
                                brand, shopify_id: l.shopify_id,
                                order_number: l.order_number, tracking_number: l.awb
                              });
                              toast.success(res.data.message || `${l.order_number} fulfilled`);
                            } catch (e) { toast.error(`Fulfill failed: ${e.response?.data?.detail || e.message}`); }
                          }}>
                          Push AWB
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              </div>
            </>
          )}
          {labels.length===0&&!labelsLoading && <div className="bg-white rounded-xl border p-10 text-center"><Package className="w-10 h-10 mx-auto mb-3 text-slate-300"/><h3 className="text-lg font-semibold text-slate-700 mb-1">No labels for this date</h3><p className="text-sm text-slate-500">Labels generated in Ship Orders will appear here. Use the date tabs above to browse history.</p></div>}
        </div>
      )}

      {/* SHIPPING DOCUMENTS */}
      {dlTab==="print" && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-xs text-blue-700">
            Upload the combined invoices PDF — labels are loaded automatically from the Labels tab for the selected date.
          </div>

          <div className="bg-white rounded-xl border p-5">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-1">
                <h4 className="text-sm font-semibold flex items-center gap-2"><FileText className="w-4 h-4 text-blue-500"/>Combined Invoice PDF (with AWB)</h4>
                <p className="text-xs text-slate-400 mt-0.5">One PDF containing all invoices for the orders you shipped</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Labels from:</span>
                <select value={shipDate} onChange={e => setShipDate(e.target.value)}
                  className="border rounded-lg px-3 py-1.5 text-xs font-medium bg-white focus:outline-none focus:ring-2 focus:ring-slate-300">
                  {labelDates.length > 0 ? labelDates.map(d => (
                    <option key={d.date} value={d.date}>
                      {new Date(d.date + "T12:00:00").toLocaleDateString("en-IN", {day:"2-digit", month:"short", year:"numeric"})} ({d.count} labels)
                    </option>
                  )) : (
                    <option value={shipDate}>{new Date(shipDate + "T12:00:00").toLocaleDateString("en-IN", {day:"2-digit", month:"short", year:"numeric"})}</option>
                  )}
                </select>
              </div>
            </div>
            <label className="cursor-pointer">
              <input type="file" accept=".pdf" onChange={handleShipInvoiceUpload} className="hidden"/>
              <Button asChild variant="outline" className="w-full" size="sm" disabled={uploadingShip}>
                <span>
                  {uploadingShip
                    ? <><RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin"/>Uploading & matching...</>
                    : session?.invoice_count > 0 && session?.label_count > 0
                      ? <><CheckCircle className="w-3.5 h-3.5 mr-1 text-emerald-500"/>{session.invoice_count} invoices matched with {session.label_count} labels · Re-upload to replace</>
                      : <><Upload className="w-3.5 h-3.5 mr-1"/>Upload Invoice PDF</>}
                </span>
              </Button>
            </label>
          </div>

          {(session?.invoice_count > 0 || session?.label_count > 0) && (
            <div className="flex justify-end">
              <Button variant="ghost" size="sm" onClick={handleClearSession} className="text-red-500 hover:text-red-700 hover:bg-red-50 text-xs">
                <Trash2 className="w-3 h-3 mr-1"/>Clear Session
              </Button>
            </div>
          )}

          {hasMatched && (
            <div className="bg-white rounded-xl border overflow-hidden">
              <div className="px-5 py-3 border-b"><h3 className="font-semibold text-slate-800 text-sm">Matched Orders ({orders.length})</h3></div>
              <div className="max-h-72 overflow-y-auto">
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead className="text-xs uppercase">Order</TableHead>
                    <TableHead className="text-xs uppercase">Customer</TableHead>
                    <TableHead className="text-xs uppercase">Country</TableHead>
                    <TableHead className="text-xs uppercase">Product</TableHead>
                    <TableHead className="text-xs uppercase">Worksheet</TableHead>
                    <TableHead className="text-xs uppercase text-center">Status</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>{orders.map((order,i)=>(
                    <TableRow key={i} className={order._unmatched?"bg-red-50/50":""}>
                      <TableCell className="font-mono text-xs font-medium">{order.order_number}</TableCell>
                      <TableCell className="text-xs">{order.customer_name}</TableCell>
                      <TableCell className="text-xs"><span className="flex items-center gap-1">{order.needs_worksheet&&<Globe className="w-3 h-3 text-blue-500"/>}{order.country||"-"}</span></TableCell>
                      <TableCell className="text-xs">
                        {(order._items && order._items.length > 1)
                          ? order._items.map((item,j) => (
                              <div key={j}>{item.product_name}{item.product_color ? ` — ${item.product_color}` : ""}</div>
                            ))
                          : <>{order.product_name}{order.product_color?` — ${order.product_color}`:""}</>
                        }
                      </TableCell>
                      <TableCell className="text-xs">
                        {order.needs_worksheet
                          ? (order.worksheet_names && order.worksheet_names.length > 0)
                            ? order.worksheet_names.map((wsn,j) => (
                                <Badge key={j} className="bg-emerald-50 text-emerald-700 text-[10px] mr-1 mb-0.5"><CheckCircle className="w-2.5 h-2.5 mr-1"/>{wsn.replace(".pdf","")}</Badge>
                              ))
                            : order.worksheet_name
                              ? <Badge className="bg-emerald-50 text-emerald-700 text-[10px]"><CheckCircle className="w-2.5 h-2.5 mr-1"/>{order.worksheet_name.replace(".pdf","")}</Badge>
                              : <Badge className="bg-amber-50 text-amber-700 text-[10px]"><AlertTriangle className="w-2.5 h-2.5 mr-1"/>Missing</Badge>
                          : <span className="text-slate-300 text-[10px]">Not needed</span>}
                      </TableCell>
                      <TableCell className="text-center">
                        {order._unmatched
                          ? <Badge variant="destructive" className="text-[10px]"><XCircle className="w-2.5 h-2.5 mr-1"/>No label</Badge>
                          : <Badge className="bg-emerald-50 text-emerald-700 text-[10px]"><CheckCircle className="w-2.5 h-2.5 mr-1"/>Matched</Badge>}
                      </TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              </div>
            </div>
          )}

          {hasMatched && (
            <div className="bg-slate-800 rounded-xl p-5 text-white">
              <h3 className="font-semibold mb-1 flex items-center gap-2"><Printer className="w-5 h-5"/>Print Documents</h3>
              <p className="text-sm text-slate-400 mb-4">Step 1: normal paper. Step 2: label paper.</p>
              <div className="flex gap-3">
                <Button onClick={()=>handlePrint("normal")} disabled={!!printing} className="bg-blue-600 hover:bg-blue-700 text-white">
                  {printing==="normal"?<><RefreshCw className="w-4 h-4 mr-2 animate-spin"/>Generating...</>:<><Download className="w-4 h-4 mr-2"/>Print 1: Normal Paper</>}
                </Button>
                <Button onClick={()=>handlePrint("labels")} disabled={!!printing} variant="outline" className="border-slate-600 text-white hover:bg-slate-700">
                  {printing==="labels"?<><RefreshCw className="w-4 h-4 mr-2 animate-spin"/>Generating...</>:<><Download className="w-4 h-4 mr-2"/>Print 2: Label Paper</>}
                </Button>
              </div>
              <div className="mt-3 text-xs text-slate-500 space-y-0.5">
                <p>Normal Paper: 3× invoice + 1× label + worksheet (US/EU) per order + blank separators</p>
                <p>Label Paper: Labels only (concatenated in order)</p>
              </div>
            </div>
          )}

          {!hasMatched && (
            <div className="bg-white rounded-xl border p-10 text-center">
              <Printer className="w-10 h-10 mx-auto mb-3 text-slate-300"/>
              <h3 className="text-lg font-semibold text-slate-700 mb-1">Upload invoice to begin</h3>
              <p className="text-sm text-slate-500">Select the label date, then upload the combined invoice PDF. Labels load automatically from the Labels tab.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ExportAssist() {
  const [tab, setTab]     = useState("ship");
  const [brand, setBrand] = useState("extreme");

  const TABS = [
    {id:"ship",label:"Ship Orders",icon:Zap},
    {id:"worksheets",label:"Watch Worksheets",icon:StickyNote},
    {id:"downloads",label:"Downloads",icon:Archive},
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto space-y-5">
      <BrandToggle brand={brand} onChange={setBrand} />
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Ship className="w-5 h-5"/>Export Assist — {BRANDS.find(b=>b.id===brand)?.label}
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Ship orders, manage documents, download labels</p>
        </div>
      </div>
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab===t.id?"bg-white text-slate-800 shadow-sm":"text-slate-500 hover:text-slate-700"}`}>
            <t.icon className="w-4 h-4"/>{t.label}
          </button>
        ))}
      </div>
      {tab==="ship"       && <ShipOrdersTab  brand={brand}/>}
      {tab==="worksheets" && <WorksheetsTab  brand={brand}/>}
      {tab==="downloads"  && <DownloadsTab   brand={brand}/>}
    </div>
  );
}
